import { getAxiosApiClientByUser } from '@/lib/axiosInstance'
// import clientApi from '@/utils/clientAxios'
import { getRegionBasedBaseUrlFromUserRegion } from '@/utils/urls'
import { Question } from '../types'
import { getCurrentAccessToken } from '@/app/(main)/agency/(auth)/helper'

interface GenerateHeroVariantsPayload {
  prompt: string
}

interface GetSectionContextPayload {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  projectData: any
  sectionId: string
  editRequest: string
}

interface AnalyzeQuestionsPayload {
  user_prompt: string
  questions: Question[]
  website_type: string
  brandkit_id?: string
}

interface GenerateSectionPromptsPayload {
  user_prompt: string
  selected_hero: string
  page_name?: string // Added page_name parameter
  website_type?: string | null
  brandkit_id?: string
}

export interface GenerateImageResponse {
  message: string
  image_data: {
    url: string
    prompt: string
    aspect_ratio: string
    output_quality: number
  }[]
  count: number
  type: string
  status?: number
  result?: string
}

export const generateHeroVariants = async (
  payload: GenerateHeroVariantsPayload
) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post(
      '/ui-builder/generate-hero-variants',
      JSON.stringify(payload),
      { timeout: 300000 } // 60 seconds timeout
    )
    return response.data
  } catch (err) {
    console.error('Failed to generate hero variants:', err)
    throw new Error('Failed to generate hero variants')
  }
}

interface GenerateContentPayload {
  prompts: Array<string>
  selected_hero: string
  page_name?: string // Added page_name parameter
  website_type?: string
  page_id? :string
}

export const generateContent = async (
  payload: GenerateContentPayload,
  {
    onProgress,
    onComplete,
    includeAuth = true
  }: {
    onProgress?: (
      progress: number,
      sectionHtml: string,
      sectionIndex: number
    ) => void
    onComplete?: () => void
    includeAuth?: boolean
  } = {}
) => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }

    if (includeAuth) {
      try {
        const { createClient } = await import('@/utils/supabase/client')
        const supabase = createClient()
        const {
          data: { session }
        } = await supabase.auth.getSession()

        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`
        }
      } catch (error) {
        console.error('Error setting auth token:', error)
      }
    }

    const token = await getCurrentAccessToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    const regionBasedBaseUrl = await getRegionBasedBaseUrlFromUserRegion()
    const response = await fetch(
      `${regionBasedBaseUrl}/ui-builder/generate-content`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include'
      }
    )

    if (!response.ok) {
      throw new Error('Failed to generate content')
    }

    if (!response.body) {
      throw new Error('ReadableStream not supported in this browser.')
    }

    let fullHtml = ''
    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    let done = false

    while (!done) {
      const { value, done: doneReading } = await reader.read()
      done = doneReading

      if (done) {
        onComplete?.()
        break
      }

      const chunk = decoder.decode(value, { stream: true })

      const messages = chunk
        .split('\n\n')
        .filter(msg => msg.trim().startsWith('data:'))

      for (const message of messages) {
        try {
          const jsonStr = message.replace('data:', '').trim()
          const data = JSON.parse(jsonStr)

          if (data.type === 'init') {
            // console.log('Data type init')
          } else if (data.type === 'section') {
            const { html, section_index, progress } = data
            fullHtml += html || ''

            if (onProgress && progress) {
              onProgress(progress.percentage, html, section_index)
            }
          } else if (data.type === 'progress') {
            // Handle the new progress-only update type
            if (onProgress && data.progress) {
              onProgress(data.progress.percentage, '', 0)
            }
          } else if (data.type === 'complete') {
            if (onProgress && data.progress) {
              onProgress(data.progress.percentage, '', 0)
            }
            onComplete?.()
          }
        } catch (e) {
          console.error('Error parsing SSE message:', e)
        }
      }
    }

    return fullHtml
  } catch (error) {
    console.error('Error generating content:', error)
    throw error
  }
}

export const analyzeQuestions = async (payload: AnalyzeQuestionsPayload) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post(
      '/ui-builder/analyze',
      JSON.stringify(payload)
    )
    return response.data
  } catch (err) {
    console.error('error calling analyze api:', err)
  }
}

export const sectionPrompts = async (
  payload: GenerateSectionPromptsPayload
) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post(
      '/ui-builder/generate-section-prompts',
      JSON.stringify(payload),
      { timeout: 300000 } // 60 seconds timeout
    )
    return response.data
  } catch (err) {
    console.error('Failed to generate section prompts:', err)
    throw new Error('Failed to generate section prompts')
  }
}

interface GenerateImagePayload {
  prompt: string
  go_fast?: boolean
  megapixels?: string
  num_outputs?: number
  aspect_ratio?: string
  output_format?: string
  output_quality?: number
  num_inference_steps?: number
  agency_user_id?: string
}

export const generateImage = async (payload: GenerateImagePayload) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post<GenerateImageResponse>(
      '/image-generation/generate-image',
      {
        ...payload
        // go_fast: true,
        // megapixels: '1',
        // num_outputs: 3,
        // aspect_ratio: '1:1',
        // output_format: 'png',
        // output_quality: 100,
        // num_inference_steps: 4
      }
    )

    if (!response?.data) {
      throw new Error('No data received from image generation')
    }

    return response?.data
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error('Error generating images:', error)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let errorMessage: any =
      'I apologize, but I encountered an error generating your images.'

    if (
      error?.response?.data?.detail?.message ==
      'Image generation quota exceeded'
    ) {
      errorMessage = error.response.data.detail?.message
    } else if (
      error?.response?.data?.detail?.message?.includes('Insufficient credits')
    ) {
      errorMessage = error.response.data.detail?.message
    }

    throw new Error(errorMessage)
    // return { newState }
  }
}

interface EditSectionPayload {
  original_prompt?: string
  edit_request: string | null
  previous_sections?: string[]
  questions: Question[]
  section_html?: string
}

export const editSection = async (payload: EditSectionPayload) => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post(
      '/ui-builder/edit-section',
      JSON.stringify(payload)
    )
    return response.data
  } catch (err) {
    console.error('Failed to edit the section:', err)
  }
}

//   const response = await fetch(`${API_URL}/ui-builder/edit-section`, {
//     method: 'POST',
//     headers: {
//       'Content-Type': 'application/json'
//     },
//     body: JSON.stringify(payload)
//   })

//   if (!response.ok) {
//     throw new Error('Failed to edit the section')
//   }

//   return response.json()
// }

export const getSectionContext = async (payload: GetSectionContextPayload) => {
  const response = await fetch(`/api/edit/section-context`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  })

  if (!response.ok) {
    throw new Error('Failed to get section context')
  }

  return response.json()
}

interface WalterAPIPayload {
  query: string
  // k: number
}

interface WalterResponse {
  answer: string
  type: string
  result: string
  navigation: boolean
  status: number
  message: string
}

export const callWalterAPI = async (
  payload: WalterAPIPayload
): Promise<WalterResponse> => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post('/knowledge-base/ask-walter', payload)
    return response?.data
  } catch (error) {
    console.error('Error calling Walter AI:', error)
    throw error
  }
}

export const clearHistory = async () => {
  try {
    const apiClient = await getAxiosApiClientByUser()
    const response = await apiClient.post('/knowledge-base/clear-history')
    return response?.data
  } catch (err) {
    console.error(err)
    throw err
  }
}

export const resumeGeneration = async (
  payload: GenerateContentPayload,
  {
    onProgress,
    onComplete,
    includeAuth = true
  }: {
    onProgress?: (
      progress: number,
      sectionHtml: string,
      sectionIndex: number
    ) => void
    onComplete?: () => void
    includeAuth?: boolean
  } = {}
) => {
  try {
    const headers: HeadersInit = {
      'Content-Type': 'application/json'
    }

    if (includeAuth) {
      try {
        const { createClient } = await import('@/utils/supabase/client')
        const supabase = createClient()
        const {
          data: { session }
        } = await supabase.auth.getSession()

        if (session?.access_token) {
          headers.Authorization = `Bearer ${session.access_token}`
        }
      } catch (error) {
        console.error('Error setting auth token:', error)
      }
    }

    const token = await getCurrentAccessToken()
    if (token) {
      headers.Authorization = `Bearer ${token}`
    }

    const regionBasedBaseUrl = await getRegionBasedBaseUrlFromUserRegion()
    const response = await fetch(
      `${regionBasedBaseUrl}/ui-builder/resume-generation`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'include'
      }
    )

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      
      // Check for specific error messages
      if (errorData.detail && (
          errorData.detail.includes('no status key') ||
          errorData.detail.includes('completed'))) {
        console.log('No need to resume:', errorData.detail)
        return { status: 'completed', content: null }
      }
      
      // Handle invalid progress data format error
      if (errorData.message && errorData.message.includes('Invalid progress data format')) {
        console.log('Invalid progress data format:', errorData.message)
        return { status: 'invalid', content: null }
      }
      
      throw new Error(`Failed to resume generation: ${response.status}`)
    }

    // At this point we know we have a valid response with a generation in progress
    onProgress?.(0, '', 0)
    
    if (!response.body) {
      throw new Error('ReadableStream not supported in this browser.')
    }

    let fullHtml = ''
    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    let done = false

    while (!done) {
      const { value, done: doneReading } = await reader.read()
      done = doneReading

      if (done) {
        onComplete?.()
        break
      }

      const chunk = decoder.decode(value, { stream: true })

      const messages = chunk
        .split('\n\n')
        .filter(msg => msg.trim().startsWith('data:'))

      for (const message of messages) {
        try {
          const jsonStr = message.replace('data:', '').trim()
          const data = JSON.parse(jsonStr)

          if (data.type === 'init') {
            // Initialize with existing content if provided
            if (data.existing_content) {
              fullHtml = data.existing_content
              console.log('Received existing content from server')
            }
          } else if (data.type === 'section') {
            // For section updates, the server will provide the complete content
            // so we don't need to append anything ourselves
            if (data.full_html) {
              fullHtml = data.full_html
            } else if (data.html) {
              // Fallback to old behavior if full_html is not provided
              fullHtml += data.html
            }

            if (onProgress && data.progress) {
              onProgress(data.progress.percentage, data.html || '', data.section_index || 0)
            }
          } else if (data.type === 'progress') {
            // Handle the new progress-only update type
            if (onProgress && data.progress) {
              onProgress(data.progress.percentage, '', 0)
            }
          } else if (data.type === 'complete') {
            // For complete messages, the server will provide the final complete content
            if (data.full_html) {
              fullHtml = data.full_html
              console.log('Received complete content from server')
            }
            
            if (onProgress && data.progress) {
              onProgress(data.progress.percentage, '', 0)
            }
            onComplete?.()
          }
        } catch (e) {
          console.error('Error parsing SSE message:', e)
        }
      }
    }

    console.log('Resume generation completed with content length:', fullHtml.length)
    return { status: 'completed', content: fullHtml }
  } catch (error) {
    console.error('Error resuming content generation:', error)
    throw error
  }
}